import React from "react";
import { Button } from "@/components/ui/button";
import { Film, Bookmark, Terminal, Sparkles } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Landing() {
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/7991231/pexels-photo-7991231.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(1200px 600px at 80% 20%, rgba(225,29,72,0.15), transparent 60%)" }} />

      {/* Nav */}
      <header className="relative z-10 glass">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-5">
          <div className="flex items-center gap-2 font-display text-xl font-semibold" data-testid="brand-logo">
            <Film className="w-5 h-5 text-[#E11D48]" />
            <span>REEL<span className="text-[#E11D48]">KEEPER</span></span>
          </div>
          <Button
            onClick={handleLogin}
            data-testid="login-google-btn"
            className="bg-[#E11D48] hover:bg-rose-700 text-white rounded-full px-6 h-10 font-medium"
          >
            Sign in with Google
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-24">
        <div className="max-w-3xl fade-up">
          <p className="text-xs tracking-[0.3em] uppercase text-[#E11D48] font-bold mb-6" data-testid="hero-overline">
            Your cinematic backlog, curated
          </p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] gradient-text mb-8">
            Every movie you meant to watch.<br/>
            <span className="text-[#E11D48]">Finally organized.</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl leading-relaxed mb-10">
            Track your backlog, group by universe (MCU, Disney, Studio Ghibli, DreamWorks), tag with intent,
            and let Claude Code manage it all through your private MCP API.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleLogin}
              data-testid="hero-cta-btn"
              className="bg-[#E11D48] hover:bg-rose-700 text-white rounded-full h-12 px-8 text-base font-medium"
            >
              Start your backlog
            </Button>
            <Button
              variant="outline"
              onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-learn-btn"
              className="rounded-full h-12 px-8 bg-white/5 border-white/20 hover:bg-white/10 text-white"
            >
              How it works
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Bookmark, title: "Backlog, sorted.", desc: "Auto-fetch posters, plots and ratings from OMDB. Mark watched with one click." },
            { icon: Film, title: "Groups + Tags", desc: "Nest groups like Marvel → MCU → Infinity Saga. Tag anything — anime, rewatch, date night." },
            { icon: Terminal, title: "MCP for Claude Code", desc: "Your private API key turns every movie operation into a tool call from your terminal." },
          ].map((f, i) => (
            <div
              key={i}
              className="p-8 rounded-2xl bg-[#0F0F12] border border-white/5 hover:border-[#E11D48]/40 transition-all"
              data-testid={`feature-card-${i}`}
            >
              <f.icon className="w-6 h-6 text-[#E11D48] mb-6" />
              <h3 className="font-display text-xl font-semibold mb-3">{f.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="relative z-10 max-w-7xl mx-auto px-6 py-10 text-sm text-slate-500 border-t border-white/5 flex items-center gap-2">
        <Sparkles className="w-4 h-4" /> Built for cinephiles and terminal natives.
      </footer>
    </div>
  );
}
